import { Link } from '@remix-run/react'
import { LucideFoldVertical, LucideUnfoldVertical } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { aUser, useAtomValue, type MapFiltersType } from '~/atoms'
import { Button } from '~/components/ui'
import { getTransactionTableData, Listing, ListingTransaction } from '~/models'
import { cn, fmtName } from '~/utils'

export default function ListingTransactions({
  listing,
  filters,
}: {
  listing: Listing
  filters?: MapFiltersType
}) {
  const user = useAtomValue(aUser)
  const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({})

  const { headers, rows } = useMemo(() => {
    if (!listing.transactions) {
      return { headers: [], rows: {} }
    }
    return getTransactionTableData(
      listing.transactions!,
      listing.type === 'sale' ? 'sale' : 'lease',
      { filters, listing }
    )
  }, [listing])

  if (!user || !listing.transactions) {
    return null
  }

  const renderExtraRow = (transaction: ListingTransaction) => {
    const displayTenantRepBrokers =
      transaction.noTenantRep ||
      transaction.externalBroker ||
      (transaction.tenantRepBrokers || []).length > 0
    return (
      <tr className="border-b bg-muted/50">
        <td colSpan={99} className="px-4 pb-2 pt-2">
          <div className="grid grid-cols-12 gap-4">
            {(transaction.listingBrokers || []).length > 0 && (
              <div className="col-span-3">
                <p className="mb-1 font-medium">Listing Brokers: </p>
                {transaction.listingBrokers!.map((broker, index) => (
                  <span key={index}>
                    <Link
                      className="text-primary underline-offset-4 hover:underline"
                      to={`/brokers/${broker.slug}`}>
                      {fmtName(
                        broker.firstName,
                        broker.middleName,
                        broker.lastName
                      )}
                    </Link>
                    {index < transaction.listingBrokers!.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            )}
            {displayTenantRepBrokers && (
              <div className="col-span-3">
                <p className="mb-1 font-medium">Tenant Rep Brokers: </p>
                <p>
                  {transaction.noTenantRep
                    ? 'No Tenant Rep'
                    : transaction.externalTenantRep
                      ? `${transaction.externalBroker?.firstName} ${transaction.externalBroker?.lastName}`
                      : (transaction.tenantRepBrokers || []).map(
                          (broker, index) => (
                            <span key={index}>
                              <Link
                                className="text-primary underline-offset-4 hover:underline"
                                to={`/brokers/${broker.slug}`}>{`${broker.firstName} ${broker.lastName}`}</Link>
                              {index < transaction.tenantRepBrokers!.length - 1
                                ? ', '
                                : ''}
                            </span>
                          )
                        )}
                </p>
              </div>
            )}
            {transaction.lease?.tenantName && (
              <div className="col-span-3">
                <p className="mb-1 font-medium">Tenant Name: </p>
                <p>{transaction.lease.tenantName}</p>
              </div>
            )}
            {transaction.sale?.terms && (
              <div className="col-span-12">
                <p className="mb-1 font-medium">Terms:</p>{' '}
                <p>{transaction.sale.terms}</p>
              </div>
            )}
            {transaction.notes && (
              <div className="col-span-12">
                <p className="mb-1 font-medium">Notes:</p>{' '}
                <p>{transaction.notes}</p>
              </div>
            )}
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="max-h-[400px] min-w-full divide-y divide-border">
        <thead>
          <tr>
            <th></th>
            {headers.map((header, index) => {
              return (
                <th
                  key={header.key}
                  className={cn(
                    'whitespace-nowrap pb-1 pr-2 text-left font-medium text-foreground',
                    index > 0 && 'pl-2'
                  )}>
                  {header.label}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody className="divide-y divide-border">
          {listing.transactions!.map((transaction, index) => {
            const row = rows[transaction._id]
            if (!row) {
              return null
            }
            const displayTenantRepBrokers =
              transaction.noTenantRep ||
              transaction.externalBroker ||
              (transaction.tenantRepBrokers || []).length > 0
            const displayExpanded =
              ((transaction.listingBrokers || []).length === 0 &&
                !displayTenantRepBrokers &&
                !transaction.notes &&
                !transaction.sale?.terms &&
                !transaction.lease?.tenantName) === false
            return (
              <React.Fragment key={transaction._id}>
                <tr key={index}>
                  {displayExpanded ? (
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mr-1 h-6 p-1"
                        onClick={() =>
                          setExpanded({
                            ...expanded,
                            [index]: !expanded[index],
                          })
                        }>
                        {expanded[index] ? (
                          <LucideFoldVertical className="h-4 w-4" />
                        ) : (
                          <LucideUnfoldVertical className="h-4 w-4" />
                        )}
                        <span className="sr-only">Fold</span>
                      </Button>
                    </td>
                  ) : (
                    <td></td>
                  )}
                  {headers.map((header, index) => (
                    <td
                      key={header.key}
                      className={cn(
                        'whitespace-nowrap px-2 py-2 ', //text-muted-foreground
                        index === 0 && 'pl-0',
                        transaction.notes && 'pb-1'
                      )}>
                      {row[header.key]}
                    </td>
                  ))}
                </tr>
                {displayExpanded &&
                  expanded[index] &&
                  renderExtraRow(transaction)}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
